import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileClock, Trash2, ExternalLink, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function ScoreBadge({ score }) {
  const color = score >= 80 ? "#00C853" : score >= 50 ? "#FFD600" : "#FF3B30";
  return (
    <span
      className="inline-flex items-center justify-center w-11 h-11 rounded-lg font-heading font-black text-lg"
      style={{ backgroundColor: `${color}1A`, color }}
    >
      {score}
    </span>
  );
}

export default function Reports() {
  const [reports, setReports] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/reports/${id}`);
      setReports((r) => r.filter((x) => x.id !== id));
      toast.success("Report deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (reports === null)
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="w-8 h-8 animate-spin text-klein" />
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Saved Reports</h1>
        <p className="text-gray-500 mt-2">Your website audit history.</p>
      </div>

      {reports.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-16 text-center" data-testid="reports-empty">
          <FileClock className="w-12 h-12 mx-auto text-gray-300" strokeWidth={1.25} />
          <h3 className="font-heading font-bold text-xl mt-4">No reports yet</h3>
          <p className="text-gray-500 mt-1 mb-6">Run your first audit to see it saved here.</p>
          <Button onClick={() => navigate("/")} className="bg-[#0A0A0A] hover:bg-gray-800" data-testid="run-first-audit">
            Run an audit <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden divide-y divide-gray-100" data-testid="reports-list">
          {reports.map((r) => (
            <div
              key={r.id}
              data-testid={`report-row-${r.id}`}
              onClick={() => navigate(`/reports/${r.id}`)}
              className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <ScoreBadge score={r.score} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate flex items-center gap-1.5">
                  {r.url} <ExternalLink className="w-3.5 h-3.5 text-gray-300" />
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {new Date(r.created_at).toLocaleString()} · {r.summary?.good || 0} passed · {r.summary?.warning || 0} warnings · {r.summary?.error || 0} errors
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                data-testid={`delete-report-${r.id}`}
                onClick={(e) => remove(r.id, e)}
                className="text-gray-400 hover:text-bad"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
